import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { setupAPI } from '../api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setupAPI.status()
      .then((res) => { if (res.data.needs_setup) navigate('/setup'); })
      .catch(() => {});
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success('Login successful!');
      // Redirect carriers and customers to /loads, everyone else to /dashboard
      if (userData.role === 'carrier' || userData.role === 'customer') {
        navigate('/loads');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--tms-bg)]">
      {/* Left side - Hero Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-between p-12"
        style={{ background: '#1F2B36' }}
        data-testid="login-brand-panel"
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 45%, rgba(255,255,255,0.05), transparent 70%)' }} />
        <div className="absolute -right-32 -bottom-24 w-[520px] h-[520px] pointer-events-none" style={{ background: 'repeating-linear-gradient(115deg, transparent 0 56px, rgba(255,0,0,0.10) 56px 76px)', transform: 'skewX(-12deg)' }} />
        <div className="absolute inset-0 pointer-events-none opacity-[0.07] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

        <div className="relative z-10">
          <img src="/brand/soren-logo.png" alt="Soren" className="h-14 w-auto" draggable={false} data-testid="login-soren-logo" />
        </div>

        <div className="relative z-10 flex items-center justify-center" style={{ animation: 'loginFadeUp 700ms ease-out both' }}>
          <img src="/brand/tagline.png" alt="Create a better experience" className="w-full max-w-[560px] drop-shadow-[0_12px_30px_rgba(0,0,0,0.45)]" draggable={false} data-testid="login-tagline-image" />
        </div>

        <div className="relative z-10 flex items-end justify-between text-slate-400">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-1">Powered by</p>
            <p className="text-sm text-slate-300" style={{ fontWeight: 800, letterSpacing: '-0.5px' }}>Mila<span style={{ color: 'var(--tms-primary)', fontWeight: 300 }}>TMS</span></p>
          </div>
          <p className="text-xs text-slate-500">Cross-border logistics, managed end to end.</p>
        </div>
        <style>{`@keyframes loginFadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-lg bg-[var(--tms-primary)] flex items-center justify-center shadow-lg">
              <Truck className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl text-[var(--tms-text)]" style={{ fontWeight: 900, letterSpacing: '-1px' }}>Mila<span style={{ color: 'var(--tms-primary)', fontWeight: 300 }}>TMS</span></h1>
              <p className="text-sm text-[var(--tms-text-muted)]">Sign in to your account</p>
            </div>
          </div>

          {/* Login Form */}
          <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[var(--tms-text)]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="bg-[var(--tms-bg)] border-[var(--tms-border)] text-[var(--tms-text)] placeholder:text-[var(--tms-text-muted)]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[var(--tms-text)]">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="bg-[var(--tms-bg)] border-[var(--tms-border)] text-[var(--tms-text)] placeholder:text-[var(--tms-text-muted)]"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[var(--tms-primary)] hover:opacity-90 text-white shadow-lg" 
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Sign In
              </Button>
            </form>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-[var(--tms-text-muted)]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[var(--tms-primary)] hover:underline font-medium" data-testid="register-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
